/*
 *  Links List - Create a list of links, and then share it!
 *  Copyright (c) Luke Denton
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation; either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

/**
 * Abstract interface class for local storage
 */
export default class DataStore {
  constructor (config = {}) {
    this.config = config;
  }

  /**
   * Sync provided state with localStorage
   *
   * @param {Object} state
   *
   * @returns {Promise}
   */
  static persist(state) {
    return new Promise(() => {
      if (state.links.length === 0) {
        // I'm pretty sure I'm also doing this somewhere else too, or at least I was prior to the refactor that split
        // adding and removing links
        DataStore.clear();
        return;
      }

      localStorage.setItem('links-list', JSON.stringify(state));
    });
  }

  static getStored() {
    const stored = localStorage.getItem('links-list');
    if (!stored) return false;

    return JSON.parse(stored);
  }

  /**
   * Get the view options stored locally for this user
   *
   * @returns {*[]|any}
   */
  static getListViews() {
    const localViews = localStorage.getItem('links-list-views');
    if (!localViews) {
      return [];
    }

    return JSON.parse(localViews);
  }

  /**
   * Get the locally saved view preference, if any, for the given list ID
   *
   * @param {string} listId
   *
   * @returns {string|boolean}
   */
  static getLocalListView(listId) {
    const localView = DataStore.getListViews().find(listView => listView.listId === listId);
    if (!localView) {
      return false;
    }

    return localView.view
  }

  /**
   * Set the view for a list
   * This will be stored locally for a user who is viewing a locked list, and isn't the author of the list
   *
   * @param {string} listId The ID of the list that the view has been changed for
   * @param {string} view   The view that has been changed to
   */
  static setListView(listId, view) {
    const localViews = DataStore.getListViews();
    const localView = {
      listId, view
    };
    const updatedLocalViews = localViews.filter(localView => localView.id !== listId); // Remove the old saved view for this list, if any

    localStorage.setItem('links-list-views', JSON.stringify([...updatedLocalViews, localView]))

  }

  /**
   * Clear local storage value
   * This will likely be called once the user has generated a URL, so the data will be saved to Firebase instead
   *
   * @returns {null}
   */
  static clear() {
    localStorage.removeItem('links-list');
  }
}
